/**
 * Сквозное отслеживание источника трафика (ТЗ).
 * Cookie traffic_source, срок 365 дней, при внутренних переходах не перезаписывается.
 */

const COOKIE_NAME = 'traffic_source';
const COOKIE_DAYS = 365;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const SEARCH_DOMAINS = [
  { pattern: /google\.(com|ru|kz|by)/i, name: 'google' },
  { pattern: /(yandex\.(ru|kz|com)|ya\.ru)/i, name: 'yandex' },
  { pattern: /bing\.com/i, name: 'bing' },
  { pattern: /go\.mail\.ru/i, name: 'mail.ru' },
  { pattern: /duckduckgo\.com/i, name: 'duckduckgo' },
  { pattern: /yahoo\.com/i, name: 'yahoo' },
];

const SOCIAL_DOMAINS = [
  { pattern: /(instagram\.com|l\.instagram\.com)/i, name: 'instagram' },
  { pattern: /(linkedin\.com|lnkd\.in)/i, name: 'linkedin' },
  { pattern: /(facebook\.com|fb\.com|l\.facebook\.com)/i, name: 'facebook' },
  { pattern: /(t\.me|telegram\.org)/i, name: 'telegram' },
  { pattern: /(youtube\.com|youtu\.be)/i, name: 'youtube' },
  { pattern: /(twitter\.com|x\.com)/i, name: 'twitter' },
  { pattern: /tiktok\.com/i, name: 'tiktok' },
];

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = name + '=' + encodeURIComponent(value) + ';path=/;expires=' + d.toUTCString() + ';SameSite=Lax';
}

function sanitizeUtmParam(v) {
  if (v == null) return '';
  return String(v).replace(/[\r\n\t]+/g, '').trim();
}

function getUtmFromUrl() {
  if (typeof window === 'undefined' || !window.location) return null;
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  let hasAny = false;
  for (const key of UTM_KEYS) {
    const raw = params.get(key);
    const v = sanitizeUtmParam(raw);
    if (v) {
      utm[key] = v;
      hasAny = true;
    }
  }
  return hasAny ? utm : null;
}

function getReferrerHost() {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    return new URL(document.referrer).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function getCurrentOriginHost() {
  if (typeof window === 'undefined' || !window.location) return '';
  try {
    return new URL(window.location.origin).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function isInternalNavigation(referrerHost) {
  if (!referrerHost) return false;
  const current = getCurrentOriginHost();
  if (!current) return false;
  return referrerHost === current || referrerHost.endsWith('.' + current) || current.endsWith('.' + referrerHost);
}

function detectSearchEngine(host) {
  if (!host) return null;
  for (const { pattern, name } of SEARCH_DOMAINS) {
    if (pattern.test(host)) return { type: 'organic', value: 'organic/' + name };
  }
  return null;
}

function detectSocial(host) {
  if (!host) return null;
  for (const { pattern, name } of SOCIAL_DOMAINS) {
    if (pattern.test(host)) return { type: 'social', value: 'social/' + name };
  }
  return null;
}

function getReferrerSource() {
  const host = getReferrerHost();
  if (!host) return null;
  const organic = detectSearchEngine(host);
  if (organic) return organic;
  const social = detectSocial(host);
  if (social) return social;
  return { type: 'referral', value: 'referral/' + host };
}

function formatFirstVisit() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' +
    pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds());
}

/**
 * Определяет данные источника для первого визита (или при force_source).
 * Приоритет: 1) force_source 2) UTM из URL 3) реферер (organic/social/referral) 4) direct
 */
function buildTrafficSourceData(forceSourceValue) {
  const landing_page = typeof window !== 'undefined' && window.location
    ? (window.location.pathname || '/') + (window.location.search || '')
    : '/';
  const first_visit = formatFirstVisit();
  const first_referrer = typeof document !== 'undefined' && document.referrer ? document.referrer : '';

  if (forceSourceValue) {
    return {
      source_type: 'force',
      source_value: forceSourceValue,
      first_referrer,
      landing_page,
      first_visit,
    };
  }

  const utm = getUtmFromUrl();
  if (utm && Object.keys(utm).length > 0) {
    return {
      ...utm,
      source_type: 'utm',
      source_value: [utm.utm_source, utm.utm_medium, utm.utm_campaign].filter(Boolean).join(' | ') || 'utm',
      first_referrer,
      landing_page,
      first_visit,
    };
  }

  if (isInternalNavigation(getReferrerHost())) {
    return null;
  }

  const refSource = getReferrerSource();
  if (refSource) {
    return {
      source_type: refSource.type,
      source_value: refSource.value,
      first_referrer,
      landing_page,
      first_visit,
    };
  }

  return {
    source_type: 'direct',
    source_value: 'direct',
    first_referrer: '',
    landing_page,
    first_visit,
  };
}

/**
 * Инициализация: если cookie нет — определяем источник и сохраняем.
 * Если cookie есть — только обновляем срок жизни (источник не перезаписываем).
 * Параметр ?force_source=... в URL перезаписывает источник.
 */
export function initTrafficSource() {
  if (typeof document === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  const forceSource = urlParams.get('force_source');

  let existing = getCookie(COOKIE_NAME);
  if (forceSource) {
    const data = buildTrafficSourceData(forceSource);
    if (data) {
      setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS);
    }
    return;
  }

  // Любые utm_* в текущем URL — актуальная атрибуция кампании (перезапись cookie).
  // Иначе старый визит без меток или другая рассылка блокировала бы новые UTM до истечения года.
  if (getUtmFromUrl()) {
    const data = buildTrafficSourceData(null);
    if (data) {
      setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS);
    }
    return;
  }

  if (existing) {
    try {
      JSON.parse(existing);
      setCookie(COOKIE_NAME, existing, COOKIE_DAYS);
      return;
    } catch (_) {
      existing = null;
    }
  }

  const data = buildTrafficSourceData(null);
  if (data) {
    setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS);
  }
}

/**
 * Возвращает строку для поля utm (всё в одном поле, не разделяя).
 * Формат: JSON-строка объекта с source_type, source_value, first_referrer, landing_page, first_visit, utm_*.
 */
export function getTrafficSourceForForm() {
  if (typeof document === 'undefined') return '';
  const raw = getCookie(COOKIE_NAME);
  if (!raw) {
    const data = buildTrafficSourceData(null);
    return data ? JSON.stringify(data) : '';
  }
  try {
    return raw;
  } catch {
    return raw;
  }
}
